import React, {
	useCallback,
	useRef,
	type ChangeEvent,
	type KeyboardEvent,
	type MouseEvent,
	useMemo,
} from 'react';
import isEqual from 'lodash/isEqual';
import Checkbox from '@atlaskit/checkbox';
import { Inline } from '@atlaskit/primitives/compiled';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { expVal } from '@atlassian/jira-feature-experiments';
import { BULK_OPS_SELECTED_ISSUE_LIMIT } from '@atlassian/jira-bulk-operations-common/src/common/constants.tsx';
import {
	useIssueIds,
	useChildIssueIds,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import {
	useIsIssueSelected,
	useIssueTableSelectedIssuesActions,
	useIssueTableSelectedIssues,
	useSelectedIssuesState,
	useAreAllIssuesSelected,
	useIsIssueSelectedOld,
	useIsHeaderCheckboxIndeterminate,
} from '../../../../controllers/selected-issues/index.tsx';

import { messages } from './messages.tsx';
import type { CheckboxCellProps, HeaderCheckboxCellProps } from './types.tsx';

const EMPTY_ARRAY: string[] = [];

export const CheckboxCell = ({ issueId, rowIndex, depth, ...checkboxProps }: CheckboxCellProps) => {
	const { formatMessage } = useIntl();
	const isIssueSelected = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsIssueSelected({ rowIndex, issueId, depth: depth ?? 0 })
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsIssueSelectedOld(issueId);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const flatListIssueIds = useIssueIds();

	const { selectedOnlyIssueId, checkIssueRow } = useIssueTableSelectedIssuesActions();
	const isShiftPressed = useRef(false);

	const onCheckboxChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>, analyticsEvent: UIAnalyticsEvent) => {
			const toggleCheckboxChecked = e.target.checked;
			fireUIAnalytics(analyticsEvent, 'newIssueNavigatorTableRow', {
				isCheckboxChecked: toggleCheckboxChecked,
				selectionType: isShiftPressed.current ? 'shift' : 'default',
			});
			checkIssueRow(
				issueId,
				toggleCheckboxChecked,
				isShiftPressed.current,
				flatListIssueIds,
				expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false) ? depth : undefined,
			);
		},
		[checkIssueRow, issueId, flatListIssueIds, depth],
	);

	const handleSelectedOnlyIssueId = useCallback(
		(ev: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>) => {
			ev.preventDefault();
			const analyticsEvent = createAnalyticsEvent({
				actionSubject: 'checkbox',
				action: 'changed',
			});
			fireUIAnalytics(analyticsEvent, 'newIssueNavigatorTableRow', {
				isCheckboxChecked: true,
				selectionType: 'ctrl',
			});
			selectedOnlyIssueId(issueId);
		},
		[createAnalyticsEvent, selectedOnlyIssueId, issueId],
	);

	return (
		<Inline alignInline="center" data-vc="checkbox-cell">
			<Checkbox
				{...checkboxProps}
				aria-label={formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.checkboxAriaLabelIssueTermRefresh
						: messages.checkboxAriaLabel,
				)}
				isChecked={isIssueSelected}
				onChange={onCheckboxChange}
				onClick={(ev) => {
					isShiftPressed.current = ev.shiftKey;
					abortAll('new_interaction');
				}}
				onKeyDown={(ev) => {
					if (!isIssueSelected && ev.ctrlKey && ev.code === 'Space') {
						handleSelectedOnlyIssueId(ev);
					}
				}}
				onContextMenu={(ev) => {
					if (!isIssueSelected && ev.ctrlKey) {
						handleSelectedOnlyIssueId(ev);
					}
				}}
			/>
		</Inline>
	);
};

export const HeaderCheckboxCell = ({
	groupByFieldId,
	totalIssueSearchResultCount,
	...checkboxProps
}: HeaderCheckboxCellProps) => {
	const { formatMessage } = useIntl();
	const selectedIssueIdsOld = useIssueTableSelectedIssues();
	const { excludedIssueIds, selectedIssueIds } = expVal(
		'bulk_ops_select_all_beyond_experiment',
		'isEnabled',
		false,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSelectedIssuesState()
		: { excludedIssueIds: new Set(), selectedIssueIds: [] };
	const { insertSelectedIssueIds, reset, selectAllIssues } = useIssueTableSelectedIssuesActions();
	const childIssueIds = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useChildIssueIds()
		: EMPTY_ARRAY;

	const prevGroupByFieldId = useRef(groupByFieldId);

	if (prevGroupByFieldId.current !== groupByFieldId) {
		prevGroupByFieldId.current = groupByFieldId;
		reset();
	}

	const issueIds = useIssueIds();

	const isChecked = useAreAllIssuesSelected(issueIds);

	const allUnloadedOrLoadedChilds = useMemo(
		() => new Set([...childIssueIds, ...selectedIssueIds]),
		[childIssueIds, selectedIssueIds],
	);

	const isIndeterminate = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsHeaderCheckboxIndeterminate({ isChecked, allUnloadedOrLoadedChilds })
		: !isChecked && selectedIssueIdsOld.length > 0;

	const onCheckboxChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>, analyticsEvent: UIAnalyticsEvent) => {
			const toggleCheckboxChecked = e.target.checked;
			fireUIAnalytics(analyticsEvent, 'bulkSelectTableRows', {
				isCheckboxChecked: toggleCheckboxChecked,
			});
			if (isIndeterminate || !isChecked) {
				if (expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)) {
					const exceedsLimit =
						totalIssueSearchResultCount &&
						totalIssueSearchResultCount >= BULK_OPS_SELECTED_ISSUE_LIMIT;
					const hasInteracted =
						excludedIssueIds.size > 0 || !isEqual(selectedIssueIds, allUnloadedOrLoadedChilds);

					if (isIndeterminate && exceedsLimit && !hasInteracted) {
						reset();
					} else {
						selectAllIssues({
							childIds: childIssueIds,
							visibleIds: issueIds,
							count: totalIssueSearchResultCount,
						});
					}
				} else {
					insertSelectedIssueIds(issueIds);
				}
			} else if (isChecked) {
				reset();
			}
		},
		[
			isIndeterminate,
			isChecked,
			totalIssueSearchResultCount,
			reset,
			selectAllIssues,
			insertSelectedIssueIds,
			issueIds,
			childIssueIds,
			excludedIssueIds,
			selectedIssueIds,
			allUnloadedOrLoadedChilds,
		],
	);

	return (
		<Inline alignInline="center" data-vc="header-checkbox-cell">
			<Checkbox
				{...checkboxProps}
				aria-label={formatMessage(messages.headerCheckboxAriaLabel)}
				isChecked={isChecked}
				isIndeterminate={isIndeterminate}
				onChange={onCheckboxChange}
				onClick={() => {
					abortAll('new_interaction');
				}}
			/>
		</Inline>
	);
};
